
import {
  getVideoPlayUrl,
  getVideoResolve
} from '@/components/gallery/gallery.service'
import { VideoResolve, Videos } from '@/models'
import moment from 'moment'
import { Component, Vue, Watch } from 'vue-property-decorator'

@Component
export default class GalleryFile extends Vue {
  public id: string | null = null
  public videoResolve = {} as VideoResolve
  public currentVideo = {} as Videos
  public videos: Videos[] = []
  loading = false
  playUrl: string | null = null
  error: string | null = null

  // to avoid infinite refresh loops when SAS expires
  private refreshAttempted = false

  $refs!: {
    player: HTMLVideoElement
  }

  async created() {
    this.id = String(this.$route.params.id || '')
    await Promise.all([this.loadPlayUrl(), this.getVideos()])
  }

  @Watch('$route.params.id')
  async onRouteIdChanged(newId: string | string[]) {
    const id = Array.isArray(newId) ? newId[0] : newId
    if (!id || String(id) === this.id) return

    this.id = String(id)
    this.refreshAttempted = false
    this.playUrl = null
    this.error = null
    await Promise.all([this.loadPlayUrl(), this.getVideos()])
  }

  get playerEl(): HTMLVideoElement | null {
    return (this.$refs.player as HTMLVideoElement) || null
  }

  async loadPlayUrl(): Promise<void> {
    if (!this.id) {
      this.playUrl = null
      this.error = 'Missing video id.'
      return
    }

    this.loading = true
    this.error = null

    try {
      const { url } = await getVideoPlayUrl(this.id)
      this.playUrl = url
      this.refreshAttempted = false
    } catch (e) {
      this.playUrl = null
      this.error = this.normalizeError(e)
    } finally {
      this.loading = false
    }
  }

  async onVideoError(): Promise<void> {
    const v = this.playerEl
    if (!v) return

    // If SAS expired, browser often throws a generic media error.
    // We do one refresh attempt.
    if (this.refreshAttempted) {
      this.error = 'Video could not be loaded. Please try again.'
      return
    }

    this.refreshAttempted = true

    const resumeAt = Number.isFinite(v.currentTime) ? v.currentTime : 0
    const wasPlaying = !v.paused

    try {
      const { url } = await getVideoPlayUrl(this.id)
      this.playUrl = url

      this.$nextTick(() => {
        const vv = this.playerEl
        if (!vv) return

        const onLoaded = () => {
          vv.removeEventListener('loadedmetadata', onLoaded)
          try {
            vv.currentTime = resumeAt
          } catch {
            // ignore seek errors before seekable
          }
          if (wasPlaying) {
            vv.play().catch(() => {
              // Autoplay may require user gesture
            })
          }
        }

        vv.addEventListener('loadedmetadata', onLoaded)
        vv.load()
      })
    } catch (e) {
      this.error = this.normalizeError(e)
    }
  }

  private normalizeError(e: any): string {
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      'Unexpected error.'
    return typeof msg === 'string' ? msg : 'Unexpected error.'
  }

  async getVideos() {
    if (!this.id) return
    this.videoResolve = await getVideoResolve(+this.id)
    this.currentVideo = this.videoResolve.video
    this.videos = this.videoResolve.videos.filter(
      c => c.videoID != this.currentVideo.videoID
    )
  }

  goToGallery() {
    this.$router.push({ name: 'Gallery' })
  }

  goToMedia(a: string | number) {
    this.$router.push({ name: 'GalleryFile', params: { id: String(a) } })
  }

  public formatDate(value: string) {
    return moment(value).format('MM/DD/YYYY')
  }
}
